import { isActionComplete } from './action-panel/action-draft';
import { isLeafComplete } from './condition-panel/condition-draft';
import {
  EditorActionBranch,
  EditorConditionNode,
  EditorRule,
  isConfiguredAction,
} from './editor-model';
import { isRuleEmpty } from './normalize';

/**
 * Pure validation of the rules working copy, keyed by node identity. Metadata
 * validation stays in RHF. Still-empty placeholder rules and `tooDeep` rules
 * are exempt.
 */

export interface ActionErrorAddress {
  branch: EditorActionBranch;
  index: number;
}

/** Errors for one rule. */
export interface RuleErrors {
  /** Ids of incomplete condition leaves (missing value, or subProperty on a key-value property). */
  conditionLeaves: string[];
  /** Addresses of actions (either branch) missing a required param. */
  actions: ActionErrorAddress[];
  /** A real rule staging nothing at all — no conditions or actions. */
  empty: boolean;
}

export interface WorkflowErrors {
  /** Keyed by `ruleIndex`; error-free rules are absent, so `rules[i]` is the has-error test. */
  rules: Record<number, RuleErrors>;
}

const isExempt = (rule: EditorRule): boolean =>
  rule.tooDeep || (rule.placeholder && isRuleEmpty(rule));

const incompleteLeafIds = (node: EditorConditionNode, into: string[]): void => {
  if (node.kind === 'leaf') {
    if (!isLeafComplete(node)) {
      into.push(node.id);
    }
    return;
  }
  node.children.forEach((child) => incompleteLeafIds(child, into));
};

const incompleteActionAddresses = (rule: EditorRule): ActionErrorAddress[] => {
  const addresses: ActionErrorAddress[] = [];
  (['actions', 'elseActions'] as const).forEach((branch) => {
    rule[branch].forEach((action, index) => {
      // Typeless scaffolding never saves, so it has nothing to complete.
      if (isConfiguredAction(action) && !isActionComplete(action)) {
        addresses.push({ branch, index });
      }
    });
  });
  return addresses;
};

const validateRule = (rule: EditorRule): RuleErrors | null => {
  if (isExempt(rule)) {
    return null;
  }
  const conditionLeaves: string[] = [];
  incompleteLeafIds(rule.condition, conditionLeaves);
  const actions = incompleteActionAddresses(rule);
  const empty = isRuleEmpty(rule);
  if (conditionLeaves.length === 0 && actions.length === 0 && !empty) {
    return null;
  }
  return { conditionLeaves, actions, empty };
};

export const validateWorkflow = (rules: EditorRule[]): WorkflowErrors => {
  const result: WorkflowErrors = { rules: {} };
  rules.forEach((rule, ruleIndex) => {
    const errors = validateRule(rule);
    if (errors) {
      result.rules[ruleIndex] = errors;
    }
  });
  return result;
};

export const hasWorkflowErrors = (errors: WorkflowErrors): boolean =>
  Object.keys(errors.rules).length > 0;

export const firstInvalidRuleIndex = (errors: WorkflowErrors): number | null => {
  const indices = Object.keys(errors.rules).map(Number);
  return indices.length === 0 ? null : Math.min(...indices);
};
